// @refresh reset
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  useGetMe,
  useListPullers,
  useListEvents,
  useListInventory,
  getListInventoryQueryKey,
  type Puller,
} from "@workspace/api-client-react";

const STORAGE_KEY = "tex-global-puller";
const SHOW_BAD_PHONES_KEY = "tex-global-show-bad-phones";
export const ALL_PULLERS = "all";

type Selection = typeof ALL_PULLERS | number;

interface PullerFilterContextValue {
  /** Selected puller id, or null when "All Pullers". */
  pullerId: number | null;
  /** Resolved puller name, or null when "All Pullers". */
  pullerName: string | null;
  isAll: boolean;
  /** Raw value for the header dropdown ("all" or a stringified id). */
  selectValue: string;
  setPuller: (next: Selection) => void;
  pullers: Puller[];
  /**
   * Global default filter: when false (the default), inventory with a bad phone
   * status is hidden across both the By Event and By Account workspace views.
   */
  showBadPhones: boolean;
  setShowBadPhones: (next: boolean) => void;
}

function readShowBadPhones(): boolean {
  if (typeof window === "undefined") return true;
  const stored = window.localStorage.getItem(SHOW_BAD_PHONES_KEY);
  if (stored === null) return true;
  return stored === "true";
}

const PullerFilterContext = createContext<PullerFilterContextValue | null>(null);

function readStored(): Selection | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === null) return null;
  if (raw === ALL_PULLERS) return ALL_PULLERS;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function PullerFilterProvider({ children }: { children: ReactNode }) {
  const { data: me } = useGetMe();
  // Only surface pullers who currently have remaining inventory in a configured
  // event; this keeps the header dropdown free of inactive-puller noise.
  const { data: pullers } = useListPullers({ activeOnly: true });
  // Full (unfiltered) list, used only to resolve the name of a selected puller
  // that has dropped off the active list so we can keep them selectable.
  const { data: allPullers } = useListPullers();
  // null = not yet initialized (still resolving the per-user default).
  const [selection, setSelection] = useState<Selection | null>(() => readStored());
  const [showBadPhones, setShowBadPhonesState] = useState<boolean>(() =>
    readShowBadPhones(),
  );

  const setShowBadPhones = useCallback((next: boolean) => {
    setShowBadPhonesState(next);
    try {
      window.localStorage.setItem(SHOW_BAD_PHONES_KEY, String(next));
    } catch {
      /* best-effort persistence */
    }
  }, []);

  // First-load default: follow the puller mapped to the logged-in user, but
  // only when the operator hasn't already made (and persisted) a choice.
  useEffect(() => {
    if (selection !== null) return;
    if (!me) return;
    setSelection(me.pullerId ?? ALL_PULLERS);
  }, [me, selection]);

  const setPuller = useCallback((next: Selection) => {
    setSelection(next);
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        next === ALL_PULLERS ? ALL_PULLERS : String(next),
      );
    } catch {
      /* best-effort persistence */
    }
  }, []);

  const effective: Selection = selection ?? ALL_PULLERS;
  const pullerId = effective === ALL_PULLERS ? null : effective;

  // Active pullers, plus a guarantee that (a) the operator's own puller and
  // (b) the currently-selected puller stay in the list even if they have no
  // current inventory — so the operator is never silently locked out / reset.
  const list = useMemo(() => {
    const result = [...(pullers ?? [])];
    const ensure = (id: number | null | undefined) => {
      if (id == null) return;
      if (result.some((p) => p.id === id)) return;
      const fromAll = (allPullers ?? []).find((p) => p.id === id);
      if (fromAll) {
        result.push(fromAll);
      } else if (me?.pullerId === id) {
        result.push({ id, name: me.pullerName ?? `Puller ${id}` } as Puller);
      }
    };
    ensure(me?.pullerId);
    ensure(pullerId);
    return result.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
  }, [pullers, allPullers, me, pullerId]);

  const pullerName = useMemo(() => {
    if (pullerId == null) return null;
    const found = list.find((p) => p.id === pullerId);
    if (found) return found.name;
    // Pullers list not loaded yet, but the id matches the current user mapping.
    if (me?.pullerId === pullerId) return me.pullerName ?? null;
    return null;
  }, [pullerId, list, me]);

  const value = useMemo<PullerFilterContextValue>(
    () => ({
      pullerId,
      pullerName,
      isAll: pullerId == null,
      selectValue: effective === ALL_PULLERS ? ALL_PULLERS : String(effective),
      setPuller,
      pullers: list,
      showBadPhones,
      setShowBadPhones,
    }),
    [pullerId, pullerName, effective, setPuller, list, showBadPhones, setShowBadPhones],
  );

  return (
    <PullerFilterContext.Provider value={value}>
      {children}
    </PullerFilterContext.Provider>
  );
}

export function usePullerFilter(): PullerFilterContextValue {
  const ctx = useContext(PullerFilterContext);
  if (!ctx) {
    throw new Error("usePullerFilter must be used within a PullerFilterProvider");
  }
  return ctx;
}

/**
 * Puller-scoped inventory derived state, shared by the header nav badges and the
 * workspace sidebar filtering. Fetches the selected puller's remaining inventory
 * across all events once (deduped by react-query) and scopes it to the
 * configured events. Returns nothing meaningful while "All Pullers" is active.
 */
export function usePullerScope() {
  const { pullerId, isAll } = usePullerFilter();
  const { data: events } = useListEvents();
  const { data: inventory } = useListInventory(
    { pullerId: pullerId ?? undefined },
    {
      query: {
        enabled: !isAll && pullerId != null,
        queryKey: getListInventoryQueryKey({ pullerId: pullerId ?? undefined }),
      },
    },
  );

  const eventIds = useMemo(
    () => new Set((events ?? []).map((e) => e.id)),
    [events],
  );

  // The /inventory endpoint already returns only remaining (availableQuantity>0)
  // listings; we additionally scope to configured (sidebar-visible) events.
  const scoped = useMemo(
    () =>
      (inventory ?? []).filter(
        (r) => r.eventId != null && eventIds.has(r.eventId),
      ),
    [inventory, eventIds],
  );

  const eventIdsWithInventory = useMemo(
    () => new Set(scoped.map((r) => r.eventId as number)),
    [scoped],
  );

  const accountsCount = useMemo(() => {
    const set = new Set<string>();
    for (const r of scoped) {
      const key =
        r.accountId != null
          ? `id:${r.accountId}`
          : r.vendorAccountEmail
            ? `em:${r.vendorAccountEmail.toLowerCase()}`
            : null;
      if (key) set.add(key);
    }
    return set.size;
  }, [scoped]);

  return {
    isAll,
    ready: !isAll && inventory !== undefined,
    workspaceCount: scoped.length,
    accountsCount,
    eventIdsWithInventory,
    // Full cross-event, puller-scoped inventory (configured events only). Used
    // by the By Account workspace view, which spans all of the puller's events.
    scopedInventory: scoped,
  };
}
